export interface MediaFragmentTemporal {
    beginTime: number;
    endTime: number;
}

export interface MediaFragmentSpatial {
    format: 'pixels' | 'percent';
    x: number;
    y: number;
    width: number;
    height: number;
}

export enum MediaFragmentTypeName {
    TEMPORAL = 't',
    SPATIAL = 'xywh',
}

/**
 * # MediaFragmentParser
 *
 * ## RESPONSIBILITIES
 *
 * This utility class implements the basic fragment selector types (temporal and spatial) as defined by the
 * Media Fragments URI 1.0 (basic) specification.
 *
 * It has not been properly tested against the specification, but appears to be compliant in my usage of it.
 *
 * ## SPECIFICATION
 *
 * https://www.w3.org/TR/media-frags/#media-fragment-syntax
 *
 *
 **/

export class MediaFragmentParser {

    static parseFragmentSelectorsFromUrl(url: string): Map<string, string> {

        let urlHash = MediaFragmentParser.getHashFromUrl(url);

        if(!urlHash) {
            console.warn(`MediaFragmentParser.parseFragmentSelectorsFromUrl(), URL "${url}" does not contain any media fragment selectors.`);
            return new Map();
        }

        let fragmentList = urlHash.split('&');
        let selectorPairs = fragmentList.map(item => item.split('='));
        let selectorMap: Map<string,string> = new Map();

        for (const pair of selectorPairs) {
            if(pair.length > 0) {
                selectorMap.set(pair[0], pair[1]);
            }

        }

        return selectorMap;
    }

    static parseSpatialMediaFragmentSelectorFromUrl(url: string): MediaFragmentSpatial  | undefined {

        let urlHash = MediaFragmentParser.getHashFromUrl(url);

        if(!urlHash || !MediaFragmentParser.mediaFragmentTypesInUrl(url).includes(MediaFragmentTypeName.SPATIAL)) {
            console.warn(`MediaFragmentParser.parseSpatialMediaFragmentSelectorFromUrl(), URL "${url}" does not contain a spatial media fragment selector.`);
            return;
        }


        let selectorValueOffset = urlHash.indexOf('xywh=') + 5;
        let formatPrefixLength = urlHash.indexOf(':') > 0 ?
            urlHash.indexOf(':') - selectorValueOffset + 1 : 0;

        let formatPrefixString: string = formatPrefixLength > 0 ? urlHash.slice(selectorValueOffset, formatPrefixLength) : 'percent';

        if(formatPrefixString !== 'pixels' && formatPrefixString !== 'percent') {
            console.warn(`MediaFragmentParser.parseSpatialMediaFragmentSelectorFromUrl(), format prefix has an invalid value "${formatPrefixString}". Defaulting to "percent".`)
        }

        let format: 'pixels' | 'percent' = formatPrefixString === 'pixels' || formatPrefixString === 'percent' ? formatPrefixString : 'percent';

        let coords: number[] = urlHash
            .slice(selectorValueOffset + formatPrefixLength)
            .split(',').map((position: string)=>{
                return parseFloat(position);
            });

        return {
            format,
            x: coords[0],
            y: coords[1],
            width: coords[2],
            height: coords[3]
        }
    }

    static parseTemporalMediaFragmentSelectorFromUrl(url: string): MediaFragmentTemporal  | undefined {

        let urlHash = MediaFragmentParser.getHashFromUrl(url);

        if(!urlHash || !MediaFragmentParser.mediaFragmentTypesInUrl(url).includes(MediaFragmentTypeName.TEMPORAL)) {
            console.warn(`MediaFragmentParser.parseTemporalMediaFragmentSelectorFromUrl(), URL "${url}" does not contain a temporal media fragment selector.`);
            return;
        }


        let selectorValueOffset = urlHash.indexOf('t=') + 2;
        let formatPrefixLength = urlHash.indexOf(':') > 0 ?
            urlHash.indexOf(':') - selectorValueOffset + 1 : 0;

        let timeParams: number[] = urlHash
            .slice(selectorValueOffset + formatPrefixLength)
            .split(',').map((position: string)=>{
                return parseFloat(position);
            });

        return {
            beginTime: timeParams[0],
            endTime: timeParams[1]
        }
    }

    static mediaFragmentTypesInUrl(url: string): MediaFragmentTypeName[] {
        let urlHash = MediaFragmentParser.getHashFromUrl(url);

        if(!urlHash) {
            return [];
        }

        let types: MediaFragmentTypeName[] = [];
        let selectors = urlHash.split('&').map(selector => selector.split('='));

        for(let selector of selectors) {

            switch (selector[0]) {
                case MediaFragmentTypeName.TEMPORAL:
                case MediaFragmentTypeName.SPATIAL:
                    types.push(selector[0])
            }
        }



        return types;
    }

    static getHashFromUrl(url: string): string | undefined {
        let hashPosition = url.indexOf('#') + 1;
        return  hashPosition > 0 ? url.slice(hashPosition) : undefined;
    }

}
